import React from "react";

import { AlertCircle, Compass } from "lucide-react";

import { CoreResearchQuestions } from "./CoreResearchQuestions";
import { ExperimentTypeSelectorCards } from "./ExperimentTypeSelectorCards";
import { KeyAssumptionsSection } from "./KeyAssumptionsSection";
import { PrimaryHypothesisField } from "./PrimaryHypothesisField";
import {
  ExperimentType,
  contentByExperimentType,
} from "./researchFoundation.constants";
import { SuccessSignalsSection } from "./SuccessSignalsSection";

import type {
  ExperimentFormData,
  ValidationErrors,
} from "../../types/experiment.types";

type ResearchFieldStatus = "complete" | "incomplete";

interface ResearchFoundationSectionProps {
  experimentData: ExperimentFormData;
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentFormData>>;
  errors: ValidationErrors & Record<string, string>;
  validateField: (fieldName: string, value: string) => boolean;
  getFieldStatus: (value: string) => ResearchFieldStatus;
  onFieldHelp?: (fieldType: string) => void;
}

export const ResearchFoundationSection: React.FC<
  ResearchFoundationSectionProps
> = ({ experimentData, setExperimentData, errors, validateField }) => {
  const [touchedFields, setTouchedFields] = React.useState<
    Record<string, boolean>
  >({});
  const experimentType: ExperimentType =
    experimentData.experimentType || "validation";
  const config = contentByExperimentType[experimentType];
  const coreQuestions =
    experimentData.coreResearchQuestions &&
    experimentData.coreResearchQuestions.length > 0
      ? experimentData.coreResearchQuestions
      : [""];
  const keyAssumptions =
    experimentData.keyAssumptions && experimentData.keyAssumptions.length > 0
      ? experimentData.keyAssumptions
      : [""];

  const markFieldTouched = React.useCallback((fieldKey: string) => {
    setTouchedFields((previous) =>
      previous[fieldKey] ? previous : { ...previous, [fieldKey]: true },
    );
  }, []);

  return (
    <div className="space-y-8 rounded-lg border border-white/10 bg-black p-6">
      <div>
        <div className="text-sm font-semibold text-white">Experiment Type</div>
        <div className="mt-3">
          <ExperimentTypeSelectorCards
            value={experimentType}
            onChange={(value) => {
              setExperimentData((prev) => ({ ...prev, experimentType: value }));
            }}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass size={16} className="text-blue-400" />
            <span className="text-sm font-semibold text-white">
              Research Objective <span className="text-green-500">*</span>
            </span>
          </div>
        </div>
        <textarea
          value={experimentData.researchObjective || ""}
          onChange={(e) => {
            const value = e.target.value;
            markFieldTouched("researchObjective");
            setExperimentData((prev) => ({
              ...prev,
              researchObjective: value,
            }));
            validateField("researchObjective", value);
          }}
          rows={3}
          placeholder={config.objectivePlaceholder}
          className="w-full resize-none rounded-md border border-white/20 bg-white/5 px-3 py-2 text-white placeholder:text-white/40 focus:border-amber-400/50 focus:outline-none"
        />
        <p className="mt-2 text-xs text-white/50">
          What are you trying to learn from this experiment? Be specific about
          the knowledge gap you&apos;re addressing.
        </p>
        {errors.researchObjective && (
          touchedFields.researchObjective ? (
            <div className="mt-2 flex items-center gap-2">
              <AlertCircle size={14} className="text-red-400" />
              <p className="text-sm text-red-400">{errors.researchObjective}</p>
            </div>
          ) : null
        )}
      </div>

      <CoreResearchQuestions
        questions={coreQuestions}
        onChange={(questions) =>
          setExperimentData((prev) => ({
            ...prev,
            coreResearchQuestions: questions,
          }))
        }
        experimentType={experimentType}
      />

      <PrimaryHypothesisField
        value={experimentData.primaryHypothesis || ""}
        onChange={(value) => {
          markFieldTouched("primaryHypothesis");
          setExperimentData((prev) => ({ ...prev, primaryHypothesis: value }));
        }}
        experimentType={experimentType}
        error={
          touchedFields.primaryHypothesis ? errors.primaryHypothesis : undefined
        }
      />

      <KeyAssumptionsSection
        assumptions={keyAssumptions}
        onChange={(assumptions) =>
          setExperimentData((prev) => ({
            ...prev,
            keyAssumptions: assumptions,
          }))
        }
        experimentType={experimentType}
        defaultOpen
      />

      <SuccessSignalsSection
        signals={
          experimentData.successSignals || {
            qualitative: "",
            quantitative: "",
          }
        }
        onChange={(signals) =>
          setExperimentData((prev) => ({ ...prev, successSignals: signals }))
        }
        experimentType={experimentType}
      />
    </div>
  );
};
